/**
 * Keybindings settings — everything the page needs to know, computed from data.
 *
 * The server hands the client a *merged* resolved config with no provenance:
 * defaults and user overrides arrive as one flat list. Every question the page
 * asks — is this row a default or a rebind, what would Reset restore, does this
 * chord already belong to something else — is answered here by comparing that
 * list against `DEFAULT_RESOLVED_KEYBINDINGS`, so the components stay renderers.
 *
 * @module
 */
import {
  type KeybindingCommand,
  type KeybindingWhenNode,
  type ResolvedKeybindingRule,
  type ResolvedKeybindingsConfig,
  STATIC_KEYBINDING_COMMANDS,
} from "@cmmd-forge/contracts";
import {
  DEFAULT_RESOLVED_KEYBINDINGS,
  encodeShortcut,
  encodeWhenAst,
} from "@cmmd-forge/shared/keybindings";

/**
 * Where a row's binding came from.
 *
 * Only two values, because the store only has two states: a command either has
 * a custom rule claiming it or it does not. There is no third "partly
 * overridden" case — an upsert replaces every custom rule for a command.
 */
export type KeybindingSource = "Default" | "Custom";

export interface KeybindingRow {
  /** Stable across renders and unique even when one command is bound twice. */
  readonly id: string;
  readonly command: KeybindingCommand;
  /** Canonical encoded chord, e.g. `mod+shift+j`. What an upsert persists. */
  readonly key: string;
  /**
   * The same chord as structured modifiers, so the row can render it through
   * the app's existing `formatShortcutLabel` rather than re-deriving ⌘/⌃ here.
   */
  readonly shortcut: ResolvedKeybindingRule["shortcut"];
  /** Source text of the `when` clause; empty string when unconditional. */
  readonly when: string;
  /**
   * The same clause parsed, so the editor can seed its tree directly.
   *
   * Carried alongside the text rather than re-parsed at the row: the server
   * already resolved it, and parsing the printed form back would make the
   * editor's starting point depend on the printer round-tripping exactly.
   */
  readonly whenAst: KeybindingWhenNode | undefined;
  readonly source: KeybindingSource;
  /**
   * Chords this command ships with, so Reset can say what it restores.
   *
   * A list because a command may ship bound twice (`chat.new` is `mod+n` and
   * `mod+shift+o`), and empty for commands with no default at all — a project
   * script, where removal is an unbind rather than a reset.
   */
  readonly defaultKeys: ReadonlyArray<string>;
  /** Other commands that answer to the same chord in the same context. */
  readonly conflicts: ReadonlyArray<KeybindingCommand>;
}

const MODIFIER_KEYS = new Set(["Meta", "Control", "Alt", "Shift", "OS", "AltGraph"]);

/**
 * Browser `KeyboardEvent.key` values that the config spells differently.
 *
 * `parseKeybindingShortcut` lowercases and then matches literally, so anything
 * not listed here has to already be its own config spelling.
 */
const KEY_ALIASES: Readonly<Record<string, string>> = {
  " ": "space",
  escape: "esc",
  arrowup: "up",
  arrowdown: "down",
  arrowleft: "left",
  arrowright: "right",
};

/**
 * Contexts a `when` clause may name.
 *
 * Sourced from the shipped defaults rather than written out, so a context that
 * gains a default binding becomes offerable without a second edit here, and one
 * that is removed stops being offered. `terminalOpen` is listed explicitly: it
 * is a real context the evaluator understands but no default currently uses.
 */
export const WHEN_VARIABLE_OPTIONS: ReadonlyArray<string> = [
  ...new Set([
    ...DEFAULT_RESOLVED_KEYBINDINGS.flatMap((rule) =>
      rule.whenAst ? collectWhenIdentifiers(rule.whenAst) : [],
    ),
    "terminalOpen",
  ]),
].toSorted();

function collectWhenIdentifiers(node: KeybindingWhenNode): ReadonlyArray<string> {
  switch (node.type) {
    case "identifier":
      return [node.name];
    case "not":
      return collectWhenIdentifiers(node.node);
    case "and":
    case "or":
      return [...collectWhenIdentifiers(node.left), ...collectWhenIdentifiers(node.right)];
  }
}

function whenText(rule: ResolvedKeybindingRule): string {
  return rule.whenAst ? encodeWhenAst(rule.whenAst) : "";
}

/**
 * The chord-plus-context a binding occupies.
 *
 * Two bindings collide only when both parts match: `mod+d` is `terminal.split`
 * with terminal focus and `diff.toggle` without it, and treating that as a
 * conflict would flag Forge's own shipped defaults.
 */
function occupancy(rule: ResolvedKeybindingRule): string {
  return `${encodeShortcut(rule.shortcut) ?? ""}\u0000${whenText(rule)}`;
}

const DEFAULT_KEYS_BY_COMMAND = new Map<string, ReadonlyArray<string>>();
for (const rule of DEFAULT_RESOLVED_KEYBINDINGS) {
  const key = encodeShortcut(rule.shortcut);
  if (!key) continue;
  DEFAULT_KEYS_BY_COMMAND.set(rule.command, [
    ...(DEFAULT_KEYS_BY_COMMAND.get(rule.command) ?? []),
    key,
  ]);
}

const DEFAULT_OCCUPANCY_BY_COMMAND = new Map<string, ReadonlySet<string>>();
for (const rule of DEFAULT_RESOLVED_KEYBINDINGS) {
  DEFAULT_OCCUPANCY_BY_COMMAND.set(
    rule.command,
    new Set([...(DEFAULT_OCCUPANCY_BY_COMMAND.get(rule.command) ?? []), occupancy(rule)]),
  );
}

/**
 * Turn the merged resolved config into rows the page can render directly.
 *
 * Order is the server's, which puts retained defaults before custom rules —
 * stable enough to render without sorting, and sorting here would fight the
 * precedence the order encodes.
 */
export function buildKeybindingRows(
  resolved: ResolvedKeybindingsConfig,
): ReadonlyArray<KeybindingRow> {
  const commandsByOccupancy = new Map<string, ReadonlyArray<KeybindingCommand>>();
  for (const rule of resolved) {
    const slot = occupancy(rule);
    commandsByOccupancy.set(slot, [...(commandsByOccupancy.get(slot) ?? []), rule.command]);
  }

  return resolved.flatMap((rule, index) => {
    const key = encodeShortcut(rule.shortcut);
    // A rule whose shortcut cannot be re-encoded has no chord to show and no
    // chord to edit, so there is nothing honest to render for it.
    if (!key) return [];

    const slot = occupancy(rule);
    return [
      {
        id: `keybinding-${index}-${rule.command}`,
        command: rule.command,
        key,
        shortcut: rule.shortcut,
        when: whenText(rule),
        whenAst: rule.whenAst,
        source: DEFAULT_OCCUPANCY_BY_COMMAND.get(rule.command)?.has(slot) ? "Default" : "Custom",
        defaultKeys: DEFAULT_KEYS_BY_COMMAND.get(rule.command) ?? [],
        conflicts: (commandsByOccupancy.get(slot) ?? []).filter(
          (command) => command !== rule.command,
        ),
      },
    ];
  });
}

/**
 * Commands the picker may offer.
 *
 * The static list plus whatever `script.<id>.run` commands are currently bound.
 * Project scripts are a template literal in the schema, so they cannot be
 * enumerated ahead of time — the bound ones are the only ones we can name.
 */
export function keybindingCommandOptions(
  resolved: ResolvedKeybindingsConfig,
): ReadonlyArray<KeybindingCommand> {
  const scriptCommands = resolved
    .map((rule) => rule.command)
    .filter((command) => command.startsWith("script."));
  return [...new Set<KeybindingCommand>([...STATIC_KEYBINDING_COMMANDS, ...scriptCommands])];
}

/**
 * Read a chord out of a keydown, or null if there is not one yet.
 *
 * Null covers the two states a recorder must not commit on: a modifier held
 * alone (every chord passes through that state on its way in) and a bare key
 * with no modifier (which would swallow ordinary typing).
 */
export function shortcutFromKeyboardEvent(
  event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">,
  platform: { readonly isMac: boolean },
): string | null {
  if (MODIFIER_KEYS.has(event.key)) return null;

  // `mod` is whichever key the platform treats as primary, so a binding
  // recorded on a Mac still reads correctly on Windows and Linux.
  const primaryHeld = platform.isMac ? event.metaKey : event.ctrlKey;
  const secondaryHeld = platform.isMac ? event.ctrlKey : event.metaKey;

  const modifiers: string[] = [];
  if (primaryHeld) modifiers.push("mod");
  if (secondaryHeld) modifiers.push(platform.isMac ? "ctrl" : "meta");
  if (event.altKey) modifiers.push("alt");
  if (event.shiftKey) modifiers.push("shift");
  if (modifiers.length === 0) return null;

  const lowered = event.key.toLowerCase();
  return [...modifiers, KEY_ALIASES[lowered] ?? lowered].join("+");
}

/**
 * A command id rendered as a phrase.
 *
 * `terminal.toggle` is precise and unreadable in a list of thirty; the picker
 * and the rows both need something a person can scan.
 */
export function commandLabel(command: KeybindingCommand): string {
  const scriptId = /^script\.(.+)\.run$/.exec(command)?.[1];
  if (scriptId) return `Script: ${scriptId}`;

  const [namespace, ...rest] = command.split(".");
  // Both halves are camelCase identifiers: `commandPalette.toggle` reads as
  // "Command palette: Toggle", not "CommandPalette: Toggle".
  return `${humanize(namespace ?? command)}: ${humanize(rest.join("."))}`;
}

function humanize(identifier: string): string {
  // jumpToParent -> jump to parent -> Jump to parent
  const spaced = identifier.replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.length === 0 ? spaced : `${spaced[0]!.toUpperCase()}${spaced.slice(1)}`;
}
